import './lib/init';
import './lib/logger';
import {trackStorageWrites} from './Auth/persistenceGate';
import React, {CSSProperties, lazy, Suspense} from 'react';
import {createRoot} from 'react-dom/client';
import ErrorBoundaryO from "./ErrorBoundary/ErrorBoundaryO";
import {QueryClientProvider} from "@tanstack/react-query";
import {queryClient} from "./lib/queryClient";
import {getEnvironment} from "./lib/get-environment";
import 'react-tooltip/dist/react-tooltip.css'
import {captureException} from "./lib/picos/error";
import {initFaro} from "./lib/faro";
import {installTestHooks} from "./lib/testHooks";
import {MOBILE_WIDTH} from "./lib/picos/breakpoints";

let AppWrapper: React.ComponentType<any>;
let Loading: React.ComponentType<any>;

if (getEnvironment() === 'D') {
    // Development: direct import (synchronous)
    const AppWrapperModule = await import('./AppWrapper');
    AppWrapper = AppWrapperModule.default;

    const LoadingModule = await import('./ui/Loading');
    Loading = LoadingModule.default;
} else {
    // Production: lazy load
    AppWrapper = lazy(() => import('./AppWrapper'));
    Loading = lazy(() => import('./ui/Loading'));
}

initFaro();

// ComlinkWorker is defined in a vite plugin
const StorageWorker = new ComlinkWorker(new URL('./workers/storage.worker.ts', import.meta.url)); // Vite build

// Ask the user to allow permanent storage
(async () => {
	if (navigator.storage && navigator.storage.persist) {
		try {
			const isPersisted = await navigator.storage.persisted();

			if (isPersisted) console.debug('Data is already persisted');
			else {
				const result = await navigator.storage.persist();
				console.debug(`Data persisted: ${result}`);
			}
		}
		catch (err) {
			console.warn('Unable to persist storage after the user gave their assent.')
		}
	}
})();

const firstLoad = localStorage.getItem('outlaw_loaded') !== '1';

window.addEventListener("load", (event) => {
	localStorage.setItem('outlaw_loaded', '1');
});

// Detect Internet Explorer and redirect to the browser not supported page.
const msie = navigator.appName === 'Microsoft Internet Explorer' || !!(navigator.userAgent.match(/Trident/) || navigator.userAgent.match(/rv:11/));

// No, no, no
if (msie) {
	window.location.href = "/browser-not-supported.html";
}

// The storage worker

try {
	console.log('Creating storage worker');
	window.storageWorker = trackStorageWrites(StorageWorker);
}
catch (err) {
	console.error('Storage worker failed to create', err);
	captureException(err);
}

// Install Playwright/E2E test hooks on window.__test_outlaw. Tree-shaken
// out of production builds (gated by `import.meta.env.PROD` inside the
// installer).
installTestHooks();

// NOTE: pdfjs's worker is configured lazily (see lib/pdf/pdfjsLoader.ts), not
// here at boot. Importing pdfjs at this entry module pulled the ~409KB pdfjs
// main-thread engine into the eager `store` chunk for every user. It is now set
// up on first PDF view (PdfEditor) or text extraction (extractPdfText).

// React 18.0+
const container = document.getElementById('root'); // If this fails, there is nothing to do

// The root is used for the main application
const root = createRoot(container, {
    onRecoverableError: (err: any) => console.error('Recoverable error:', err),
});

// The hiddenRoot is used for off-screen printing (e.g. generating PDFs for an invoice
window.hiddenRoot = createRoot(document.getElementById('hidden-root'));

const outerStyle: CSSProperties = {
	backgroundColor: 'ghostwhite',
	fontFamily: 'arial, sans-serif',
	fontSize: '1em',
	paddingTop: '3em',
	height: '100vh',
	width: '100vw',
	animation: "fadeInAnimation ease 1s",
	animationIterationCount: "1",
	animationFillMode: "forwards"
}

const innerStyle: CSSProperties = {
	color: 'hsl(240 5.3% 26.1%)',
	display: "flex",
	flexDirection: "column",
	height: "fit-content",
	width: "fit-content",
	lineHeight: '1em',
	margin: 'auto',
	position: 'relative',
	rowGap: "2em",
}

const logoStyle = {
	fontFamily: 'Rough Riders, arial, sans-serif',
	fontSize: window.innerWidth <= MOBILE_WIDTH ? `${window.innerWidth / 12}px` : '3em',
	lineHeight: '2em',
	whiteSpace: 'wrap',
	width: 'fit-content',
	height: 'fit-content',
	margin: "1em auto 0 auto",
	textShadow: '1px 1px 3px white',
}

const progressStyle = {
	height: "fit-content", width: "fit-content", margin: "0 auto",
}


// Preload screen
function LoadingScreen() {
	const {pathname} = window.location;

    const downloadingMessage = pathname.includes('portal') ? 'Client Portal' : 'Outlaw Practice Application'
    const loadingMessage = firstLoad ? `Downloading the ${downloadingMessage}` : pathname === '/callback' ? 'Signing in' : 'Updating Application...'

    return <div style={outerStyle}>
		<div style={innerStyle}>
			<div style={logoStyle}>
				Welcome to Outlaw Practice
			</div>
			<div style={progressStyle}>
				<Suspense fallback={<div>{loadingMessage}</div>}>
					<Loading message={loadingMessage}/>
				</Suspense>
			</div>
		</div>
	</div>
}

// Render the loader until the App is ready
root.render(
	<QueryClientProvider client={queryClient}>
		<ErrorBoundaryO uniqueKey='index' shouldUseErrorFrame={true}>
			<Suspense fallback={<LoadingScreen/>}>
				{getEnvironment() === 'D' ? <AppWrapper/> : (
                    <Suspense fallback={<LoadingScreen/>}>
                        <AppWrapper/>
                    </Suspense>
                )}
			</Suspense>
		</ErrorBoundaryO>
	</QueryClientProvider>
);

// Signal to the inline recovery script in index.html that the module tree
// fully loaded and React is rendering. Its 5s sentinel timer in DOMContentLoaded
// fires recovery if this never runs (i.e. some chunk in the critical path
// failed — e.g. a poisoned SW cache returning HTML under a .js URL).
(window as typeof window & {__outlaw_app_started__?: boolean}).__outlaw_app_started__ = true;
